import { useEffect, useState } from "react";
import { FileText, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import { orderFulfilmentPolicyApi } from "../../api/orderFulfilmentPolicyApi";
import { orderPolicyProfilesApi } from "../../api/orderPolicyProfilesApi";
import { commerceSettingsStyles as styles } from "./commerceSettingsStyles";

const MODES = [
  {
    value: "notRequired",
    label: "No delivery or pickup required",
    description: "Use this for services or orders that do not require delivery or customer pickup.",
  },
  {
    value: "deliveryOnly",
    label: "Delivery only",
    description: "Customers receive their orders at a delivery address.",
    context: "Delivery details will be required when checkout capture is enabled.",
  },
  {
    value: "pickupOnly",
    label: "Pickup only",
    description: "Customers collect their orders from the business.",
    context: "No delivery address is required.",
  },
  {
    value: "deliveryOrPickup",
    label: "Delivery or pickup",
    description: "Customers can choose between delivery and pickup.",
    context: "Customers will be able to choose delivery or pickup.",
  },
];

function readError(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function editablePolicy(policy) {
  const source = policy?.revision || policy;
  return {
    mode: source.fulfilmentMode || source.mode,
    requireCheckoutReadyBeforeAcceptance: Boolean(
      source.requireCheckoutReadyBeforeAcceptance,
    ),
    checkoutFlowDefinitionId: source.checkoutFlowDefinitionId || "",
  };
}

export function OrderFulfilmentEditor({ profile = null, onProfileUpdated, onConflict }) {
  const [policy, setPolicy] = useState(null);
  const [savedPolicy, setSavedPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkoutFlows, setCheckoutFlows] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");

  async function load() {
    setLoading(true);
    setLoadError("");
    try {
      const [policyResponse, flowResponse] = await Promise.all([
        profile ? Promise.resolve(profile) : orderFulfilmentPolicyApi.get(),
        orderFulfilmentPolicyApi.listCheckoutFlows(),
      ]);
      const data = editablePolicy(policyResponse);
      setCheckoutFlows(flowResponse || []);
      setPolicy(data);
      setSavedPolicy(data);
    } catch (requestError) {
      setLoadError(
        readError(requestError, "Delivery and pickup settings could not be loaded."),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [profile?.id, profile?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed =
    policy && savedPolicy && JSON.stringify(policy) !== JSON.stringify(savedPolicy);
  const selectedMode = MODES.find(option => option.value === policy?.mode);
  const requiresFlow = policy?.mode === "deliveryOnly" || policy?.mode === "deliveryOrPickup";
  const compatibleFlows = checkoutFlows.filter(flow =>
    policy?.mode === "deliveryOrPickup"
      ? flow.supportsDeliveryOrPickup
      : flow.supportsDelivery,
  );
  const flowMissing = requiresFlow && !policy?.checkoutFlowDefinitionId;

  async function save() {
    if (!policy || saving || !changed) return;
    setSaving(true);
    setSaveError("");
    try {
      const response = profile
        ? await orderPolicyProfilesApi.update(profile.id, {
            expectedVersion: profile.version,
            name: profile.name,
            description: profile.description || null,
            fulfilmentMode: policy.mode,
            requireCheckoutReadyBeforeAcceptance:
              policy.requireCheckoutReadyBeforeAcceptance,
            checkoutFlowDefinitionId: policy.checkoutFlowDefinitionId || null,
          })
        : await orderFulfilmentPolicyApi.update(policy);
      const data = editablePolicy(response);
      setPolicy(data);
      setSavedPolicy(data);
      onProfileUpdated?.(response);
      toast.success("Delivery and pickup settings saved.");
    } catch (requestError) {
      const message = readError(
        requestError,
        "Delivery and pickup settings could not be saved.",
      );
      setSaveError(message);
      toast.error(message);
      if (requestError?.response?.status === 409) {
        onConflict?.();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {loading ? (
        <div className="flex min-h-48 items-center justify-center text-slate-500" role="status">
          <Loader2 className="mr-2 animate-spin" size={18} /> Loading settings
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <p>{loadError}</p>
          <button
            type="button"
            onClick={load}
            className={styles.secondaryButton}
          >
            <RefreshCcw size={15} /> Retry
          </button>
        </div>
      ) : (
        <section className={styles.card}>
          <fieldset>
            <legend className="px-4 pt-4 text-sm font-semibold text-slate-900 sm:px-5">
              Delivery &amp; Pickup Options
            </legend>
            <div className="mt-2 divide-y divide-slate-200 border-t border-slate-200">
              {MODES.map(option => (
                <label
                  key={option.value}
                  className={`${styles.optionRow} ${policy?.mode === option.value ? styles.optionRowSelected : ""}`}
                >
                  <input
                    type="radio"
                    name="order-fulfilment-mode"
                    value={option.value}
                    checked={policy?.mode === option.value}
                    onChange={() => {
                      setSaveError("");
                      setPolicy(current => ({
                        ...current,
                        mode: option.value,
                        checkoutFlowDefinitionId:
                          option.value === "notRequired" || option.value === "pickupOnly"
                            ? ""
                            : current.checkoutFlowDefinitionId,
                      }));
                    }}
                    className={styles.radio}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900">
                      {option.label}
                    </span>
                    <span className="mt-1 block text-sm text-slate-600">
                      {option.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {selectedMode?.context ? (
            <p className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:px-5">
              {selectedMode.context}
            </p>
          ) : null}

          {requiresFlow ? (
            <div className="border-t border-slate-200 px-4 py-4 sm:px-5">
              <label htmlFor="checkout-flow" className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <FileText size={16} aria-hidden="true" /> Checkout Whats Form
              </label>
              <p className="mt-1 text-sm text-slate-600">
                Choose the published Form customers use to provide delivery or pickup details.
              </p>
              <select
                id="checkout-flow"
                value={policy.checkoutFlowDefinitionId}
                onChange={event => {
                  setSaveError("");
                  setPolicy(current => ({
                    ...current,
                    checkoutFlowDefinitionId: event.target.value,
                  }));
                }}
                className={`${styles.field} mt-3 sm:max-w-md`}
              >
                <option value="">Select a published Whats Form</option>
                {compatibleFlows.map(flow => (
                  <option key={flow.definitionId} value={flow.definitionId}>
                    {flow.name}
                  </option>
                ))}
              </select>
              {compatibleFlows.length === 0 ? (
                <p className="mt-2 text-sm text-amber-700">
                  No eligible published checkout Form is available for this mode.
                </p>
              ) : null}
            </div>
          ) : null}

          <label className="flex cursor-pointer items-start justify-between gap-6 border-t border-slate-200 px-4 py-4 sm:px-5">
            <span className="min-w-0">
              <span className="block text-sm font-medium text-slate-900">
                Require checkout details before accepting an order
              </span>
              <span className="mt-1 block text-sm text-slate-600">
                When enabled, staff cannot accept an order until its required checkout
                details are complete.
              </span>
              {policy?.requireCheckoutReadyBeforeAcceptance ? (
                <span className="mt-2 block text-sm text-amber-700">
                  Orders with missing required checkout details cannot be accepted.
                </span>
              ) : null}
            </span>
            <input
              type="checkbox"
              checked={Boolean(policy?.requireCheckoutReadyBeforeAcceptance)}
              onChange={event => {
                setSaveError("");
                setPolicy(current => ({
                  ...current,
                  requireCheckoutReadyBeforeAcceptance: event.target.checked,
                }));
              }}
              className={styles.checkbox}
            />
          </label>

          {saveError ? (
            <p className="border-t border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 sm:px-5" role="alert">
              {saveError}
            </p>
          ) : null}

          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
            <button
              type="button"
              onClick={save}
              disabled={!changed || saving || flowMissing}
              className={styles.primaryButton}
            >
              {saving ? <Loader2 className="animate-spin" size={17} /> : null}
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </section>
      )}
    </>
  );
}
